import { addSeriesData } from "../colors.js";

/**
 * Rebuild the in-memory `series` map for a forecast from its persisted slim dict.
 *
 * The persisted shape is `{name: {color, ssid?}}` — the heavy fields (data, yMin,
 * yMax, unit, sourceUrl) are deliberately not stored and re-derive from
 * `/api/series/:name` on load. This turns the slim dict back into the full
 * per-series stubs so the chips + chart structure render immediately, before the
 * live points arrive.
 *
 * `ssid` is carried through because it is the ONE field that cannot be re-derived
 * from the name: a suggestion's persisted name is an agent-authored display label
 * ("QUAL closing price") that the legacy name resolver can't resolve, so a
 * name-only re-fetch 404s on a signal that is alive in the catalog.
 *
 * Extracted from AppInner's log-click handler so that EVERY selection path can
 * restore the same way. Previously only the terminal-status branch restored, and
 * the running-row branch returned early — so selecting a running forecast
 * inherited whatever was on screen, and the per-forecast save effect then stamped
 * that stale selection onto the newly-selected row (a baseball-game forecast
 * acquiring Fed policy signals). Making the rebuild a pure function keeps the
 * "what does this forecast's selection look like" question testable without React.
 *
 * @param {object|null|undefined} persisted — a forecast's `_series` dict.
 * @returns {Record<string, object>} name → full series stub (empty object when the
 *   forecast has no saved selection, which is itself the correct state to load).
 */
/**
 * Should the safety-net effect load `active`'s persisted selection?
 *
 * Encodes the invariant that effect exists to protect: the on-screen selection must
 * belong to the forecast it will be saved onto. `ownedForecastId` is the forecast the
 * current selection was actually LOADED for — so a mismatch means `active` moved
 * without its own selection being loaded, and the save effect would otherwise persist
 * the previous forecast's signals onto it.
 *
 * Returns false when the forecast has no persisted selection. That case is genuinely
 * ambiguous — a fresh submit legitimately carries the on-screen selection into its new
 * row — so the caller must NOT claim ownership on a false result. That is what lets a
 * late-arriving `_series` (a `?forecast=` deep link knows the id from the URL before
 * the row body is fetched) still restore on a later run instead of being stranded.
 *
 * @param {{id?: string, _series?: object}|null|undefined} active
 * @param {string|null} ownedForecastId
 * @returns {boolean}
 */
export function shouldLoadPersistedSelection(active, ownedForecastId) {
  if (!active || !active.id) return false;
  if (ownedForecastId === active.id) return false;
  const persisted = active._series;
  if (!persisted || typeof persisted !== "object") return false;
  return Object.keys(persisted).length > 0;
}

/**
 * Should a submit wipe the on-screen signal selection before the new forecast
 * becomes active?
 *
 * #2909 made the LOAD authoritative along the navigation axis, but deliberately
 * left the submit axis alone (its "Deliberately not done" note) because the
 * per-forecast SAVE effect can't tell "fresh submit with signals the user just
 * plotted" from "navigated without restoring". That ambiguity is real for an
 * effect inferring intent after the fact — but a submit is an explicit user
 * action, so the answer is knowable at the moment it happens:
 *
 *   - No active forecast → the selection is the USER'S OWN (plotted on a blank
 *     slate, or right after a CLEAR). "Plot the signals I care about, then ask a
 *     question about them" is a real workflow, so carry them into the new row.
 *   - An active forecast → the selection BELONGS TO THAT FORECAST (the load path
 *     put it there, or the save effect already stamped it onto that row). A new
 *     question is a new forecast, so it starts clean rather than inheriting —
 *     otherwise forecast A's signals ride into B and get persisted there, the
 *     exact contamination #2909 measured, just reached along the other axis.
 *
 * Same-question resubmits are the one exception: `rerun` (chat intent
 * "redo this with deep" — useChatIntent passes `active.question` verbatim) is
 * the SAME forecast run again, not a new one, so its signals stay put. Sameness is
 * compared on the submitted text as it stands BEFORE preprocess rewrites it, with
 * both sides trimmed — leading/trailing whitespace is the only normalization, so
 * any real edit to the question reads as a new one.
 *
 * Accepts either id shape. The App-level forecast projection exposes `id`, while a
 * raw run read out of the store is keyed `runId` (lib/forecast/model.ts) — and this
 * predicate is called from the command layer, on the store side of that seam. Reading
 * only `id` silently treats every active run as "nothing active", which is a false
 * negative that reads as the bug being unfixed.
 *
 * @param {{id?: string, runId?: string, question?: string}|null|undefined} active —
 *   the forecast active at submit time (live store read, not a render snapshot).
 * @param {string|null|undefined} submittedQuestion — the text being submitted, as it
 *   stands before preprocess rewrites it; compared trimmed.
 * @returns {boolean}
 */
export function shouldClearSelectionOnSubmit(active, submittedQuestion) {
  if (!active || !(active.id || active.runId)) return false;
  const prev = typeof active.question === "string" ? active.question.trim() : "";
  const next = typeof submittedQuestion === "string" ? submittedQuestion.trim() : "";
  if (prev && next && prev === next) return false;
  return true;
}

export function rebuildSeriesFromPersisted(persisted) {
  const out = {};
  if (!persisted || typeof persisted !== "object") return out;
  for (const [name, slim] of Object.entries(persisted)) {
    const stub = addSeriesData(name, (slim && slim.color) || "#33ddcc");
    if (stub) out[name] = { ...stub, ssid: (slim && slim.ssid) || null };
  }
  return out;
}
